import {
  AvailabilityData,
  HealthEvent,
  type SitePRResponse,
  useSiteHealthConfig,
  useSiteHealthEvents,
  useSitePingLatest,
  useSitePR,
  useSiteSetupActions,
  useWeatherActuals,
  WeatherActualsCoverageResponse,
} from "@inrange/building-manager-api-client";
import {
  getESysName,
  Site,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import {
  siteLocalDatetimeValueToUtcIso,
  utcIsoToSiteLocalDatetimeValue,
} from "@inrange/theme-components";
import { Icons } from "@inrange/theme-components/icons";
import { useMemo, useState } from "react";

import {
  Badge,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { WithTooltip } from "../../components/WithTooltip";
import AvailabilityExplainModal from "../AvailabilityExplainModal";
import BessAvailabilityExplainModal from "../BessAvailabilityExplainModal";
import OPRExplainModal from "../OPRExplainModal";
import { InverterOutageSpans } from "./HealthEventDetails";
import PRExplainModal from "../PRExplainModal";
import { formatAvailability, RYG_ICONS } from "../deploymentStatsUtils";
import {
  buildMonthFilterOptions,
  formatSiteDateTime,
} from "./healthDateFilters";
import HealthInvestigationCard from "./healthInvestigation/HealthInvestigationCard";
import MonitoringPortalsCard from "./siteSections/MonitoringPortalsCard";
import SitePingHistoryModal from "./SitePingHistoryModal";
import {
  AcknowledgedSolarEdgeAlertsCard,
  SolarEdgeAlertDetails,
} from "./SolarEdgeAlertDetails";
import stringToReactNodesWithLinks from "./siteSections/stringToReactNodesWithLinks";

const NO_WEATHER_ACTUALS_FALLBACK = "No weather actuals loaded.";

const siteHasPanelOrientations = (site: Site): boolean =>
  Object.values(site.generation)
    .filter((gen) => !!gen)
    .some((gen) => gen.panelOrientations.length > 0);

// Brief, tile-sized explanation of why a site has no weather actuals: whether it
// has panel orientation data (without which Phase 1 ingest has nothing to fetch)
// and whether weather-actuals ingestion is switched on. Both facts are needed to
// diagnose the empty state at a glance.
const weatherActualsNoDataExplanation = (
  hasPanelOrientations: boolean,
  ingestionEnabled: boolean
): string =>
  `No weather actuals: ${
    hasPanelOrientations
      ? "panel orientations set"
      : "no panel orientation data"
  }, ingestion ${ingestionEnabled ? "on" : "off"}.`;

// Only the explicit "no actuals" reason gets the two-fact explanation; an absent
// reason is an unexpected empty response, not proof that no actuals exist.
const prNoDataText = (
  backendReason: string | null | undefined,
  explanation: string
): string => {
  if (backendReason === NO_WEATHER_ACTUALS_FALLBACK) return explanation;
  if (!backendReason) return "No PR data available.";
  return backendReason;
};

const formatDelta = (delta: number): string => {
  const sign = delta >= 0 ? "+" : "";
  return `${sign}${delta} pct pts prev`;
};

const MetricRow = ({
  ryg,
  value,
  label,
  linkTo,
  prevMonthDelta,
  contractedPct,
  gprPct,
  designedPrPct,
  excused,
}: {
  ryg: string | null;
  value: string;
  label: string;
  linkTo?: string;
  prevMonthDelta?: number | null;
  /** The EPC figure, excused outages removed from the clock, shown under the value. */
  excused?: { ryg: string | null; value: string };
  /** @deprecated legacy availability target */
  contractedPct?: number | null;
  gprPct?: number | null;
  designedPrPct?: number | null;
}) => (
  <div>
    <div className="d-flex align-items-center gap-2">
      {ryg && (
        <span style={{ fontSize: "1.3rem" }}>{RYG_ICONS[ryg] ?? ""}</span>
      )}
      <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>{value}</span>
    </div>
    {excused && (
      <div className="small">
        {excused.ryg && RYG_ICONS[excused.ryg]} {excused.value} (excused)
      </div>
    )}
    <div className="text-muted small">
      {linkTo ? <Link to={linkTo}>{label}</Link> : label}
    </div>
    {prevMonthDelta != null && (
      <div className="text-muted small">({formatDelta(prevMonthDelta)})</div>
    )}
    {gprPct != null && (
      <div className="text-muted small">
        (target GPR {gprPct.toFixed(1)}%
        {designedPrPct != null && `, designed ${designedPrPct.toFixed(1)}%`})
      </div>
    )}
    {gprPct == null && contractedPct != null && (
      <div className="text-muted small">(vs. {contractedPct}% contract)</div>
    )}
  </div>
);

const readingsLink = (
  siteId: string,
  startDate: string,
  endDate: string,
  options?: { preset?: string; aggregation?: string }
) =>
  `/site/${siteId}/perf/readings?dataMode=processed&startDate=${startDate}&endDate=${endDate}${options?.preset ? `&preset=${options.preset}` : ""}${options?.aggregation ? `&aggregation=${options.aggregation}` : ""}`;

const AvailabilityCard = ({
  siteId,
  availability,
  isLoading,
  onExplain,
  title = "PV System Availability",
}: {
  siteId: string;
  availability: AvailabilityData | undefined | null;
  isLoading: boolean;
  onExplain: () => void;
  title?: string;
}) => {
  if (isLoading) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">{title}</h6>
        <Spinner animation="border" size="sm" />
      </Card>
    );
  }

  if (!availability) return null;

  if (!availability.lastMonth && !availability.last12Months) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">{title}</h6>
        <div className="text-muted small">
          {availability.noDataReason ?? "No availability data available."}
        </div>
      </Card>
    );
  }

  return (
    <Card body>
      <h6 className="fw-bold mb-2">{title}</h6>
      <div className="d-flex flex-column gap-1">
        {availability.lastMonth && (
          <MetricRow
            ryg={availability.lastMonth.allCauseRyg ?? null}
            value={formatAvailability(
              availability.lastMonth.allCausePct ?? null
            )}
            excused={{
              ryg: availability.lastMonth.ryg,
              value: formatAvailability(availability.lastMonth.pct),
            }}
            label={`Last month: ${availability.lastMonth.periodLabel}`}
            linkTo={readingsLink(
              siteId,
              availability.lastMonth.startDate,
              availability.lastMonth.endDate,
              { preset: "availability", aggregation: "none" }
            )}
            prevMonthDelta={availability.lastMonth.prevMonthDelta}
          />
        )}
        {availability.last12Months && (
          <MetricRow
            ryg={availability.last12Months.allCauseRyg ?? null}
            value={formatAvailability(
              availability.last12Months.allCausePct ?? null
            )}
            excused={{
              ryg: availability.last12Months.ryg,
              value: formatAvailability(availability.last12Months.pct),
            }}
            label={availability.last12Months.periodLabel}
            linkTo={readingsLink(
              siteId,
              availability.last12Months.startDate,
              availability.last12Months.endDate,
              { preset: "availability", aggregation: "monthly" }
            )}
            contractedPct={availability.last12Months.contractedPct}
          />
        )}
      </div>
      <div className="mt-2">
        <Button
          variant="link"
          size="sm"
          className="p-0 text-muted"
          onClick={onExplain}
        >
          How is this calculated?
        </Button>
      </div>
    </Card>
  );
};

const GPRCard = ({
  siteId,
  prData,
  isLoading,
  onExplain,
  noActualsExplanation,
  deemed = false,
}: {
  siteId: string;
  prData: SitePRResponse | undefined;
  isLoading: boolean;
  onExplain: () => void;
  noActualsExplanation: string;
  deemed?: boolean;
}) => {
  const title = deemed ? "GPR (deemed)" : "GPR (excluded)";
  if (isLoading) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">{title}</h6>
        <Spinner animation="border" size="sm" />
      </Card>
    );
  }

  const gpr = deemed ? prData?.gprDeemed : prData?.gpr;
  if (!gpr || (!gpr.lastMonth && !gpr.last12Months)) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">{title}</h6>
        <div className="text-muted small">
          {prNoDataText(prData?.noDataReason, noActualsExplanation)}
        </div>
      </Card>
    );
  }

  return (
    <Card body>
      <h6 className="fw-bold mb-2">{title}</h6>
      <div className="d-flex flex-column gap-1">
        {gpr.lastMonth && (
          <MetricRow
            ryg={gpr.lastMonth.ryg}
            value={`${gpr.lastMonth.pct.toFixed(1)}%`}
            label={`Last month: ${gpr.lastMonth.periodLabel}`}
            linkTo={readingsLink(
              siteId,
              gpr.lastMonth.startDate,
              gpr.lastMonth.endDate,
              { preset: "performance-ratio", aggregation: "none" }
            )}
            prevMonthDelta={gpr.lastMonth.prevMonthDelta}
            gprPct={gpr.lastMonth.gprPct}
            designedPrPct={gpr.lastMonth.designedPrPct}
          />
        )}
        {gpr.last12Months && (
          <MetricRow
            ryg={gpr.last12Months.ryg}
            value={`${gpr.last12Months.pct.toFixed(1)}%`}
            label={gpr.last12Months.periodLabel}
            linkTo={readingsLink(
              siteId,
              gpr.last12Months.startDate,
              gpr.last12Months.endDate,
              { preset: "performance-ratio", aggregation: "monthly" }
            )}
            gprPct={gpr.last12Months.gprPct}
            designedPrPct={gpr.last12Months.designedPrPct}
          />
        )}
      </div>
      {prData?.curtailment?.lastMonth &&
        prData.curtailment.lastMonth.curtailedPeriods > 0 && (
          <div className="text-muted small mt-1">
            {deemed
              ? `${prData.curtailment.lastMonth.curtailmentPct.toFixed(1)}% of periods curtailed — credited at modelled potential`
              : `${prData.curtailment.lastMonth.curtailmentPct.toFixed(1)}% of periods excluded due to curtailment`}
          </div>
        )}
      <div className="mt-2">
        <Button
          variant="link"
          size="sm"
          className="p-0 text-muted"
          onClick={onExplain}
        >
          How is this calculated?
        </Button>
      </div>
    </Card>
  );
};

const OPRCard = ({
  siteId,
  prData,
  isLoading,
  onExplain,
  noActualsExplanation,
  deemed = false,
}: {
  siteId: string;
  prData: SitePRResponse | undefined;
  isLoading: boolean;
  onExplain: () => void;
  noActualsExplanation: string;
  deemed?: boolean;
}) => {
  const title = deemed ? "OPR (deemed)" : "OPR (excluded)";
  if (isLoading) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">{title}</h6>
        <Spinner animation="border" size="sm" />
      </Card>
    );
  }

  const opr = deemed ? prData?.oprDeemed : prData?.opr;
  if (!opr || (!opr.lastMonth && !opr.last12Months)) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">{title}</h6>
        <div className="text-muted small">
          {prNoDataText(prData?.noDataReason, noActualsExplanation)}
        </div>
      </Card>
    );
  }

  return (
    <Card body>
      <h6 className="fw-bold mb-2">{title}</h6>
      <div className="d-flex flex-column gap-1">
        {opr.lastMonth && (
          <MetricRow
            ryg={opr.lastMonth.ryg}
            value={`${opr.lastMonth.pct.toFixed(1)}%`}
            label={`Last month: ${opr.lastMonth.periodLabel}`}
            linkTo={readingsLink(
              siteId,
              opr.lastMonth.startDate,
              opr.lastMonth.endDate,
              { preset: "performance-ratio", aggregation: "none" }
            )}
            prevMonthDelta={opr.lastMonth.prevMonthDelta}
            designedPrPct={opr.lastMonth.designedPrPct}
          />
        )}
        {opr.last12Months && (
          <MetricRow
            ryg={opr.last12Months.ryg}
            value={`${opr.last12Months.pct.toFixed(1)}%`}
            label={opr.last12Months.periodLabel}
            linkTo={readingsLink(
              siteId,
              opr.last12Months.startDate,
              opr.last12Months.endDate,
              { preset: "performance-ratio", aggregation: "monthly" }
            )}
            designedPrPct={opr.last12Months.designedPrPct}
          />
        )}
      </div>
      <div className="text-muted small mt-2">
        Actual / Expected (using actual weather + InRange PV model). Green at
        90% or above, amber 85-90%, red below 85%.
      </div>
      <div className="mt-2">
        <Button
          variant="link"
          size="sm"
          className="p-0 text-muted"
          onClick={onExplain}
        >
          How is this calculated?
        </Button>
      </div>
    </Card>
  );
};

const SEVERITY_COLORS: Record<string, string> = {
  red: "danger",
  amber: "warning",
};

const CHECK_TYPE_LABELS: Record<string, string> = {
  late_data: "Late data",
  no_pv_readings: "No PV readings",
  no_grid_readings: "No grid readings",
  inverter_data_quality: "Inverter quality",
  epm_data_quality: "Grid meter quality",
  reduxi_unhealthy_state: "Device state",
  battery_unresponsive: "Battery unresponsive",
  battery_meter_mismatch: "Battery meter mismatch",
  battery_power_limit_exceeded: "Battery power limit exceeded",
  import_over_enforced_limit: "Import over enforced limit",
  import_over_allowed_limit: "Import over allowed limit",
  solaredge_alert: "SolarEdge alert",
  demand_spike: "Demand spike",
  demand_drop_to_zero: "Zero demand",
  gen_drop_to_zero: "Zero generation",
  zero_export_violation: "Export violation",
  implausible_readings: "Implausible readings",
  battery_unresponsive_monthly: "Battery unresponsive",
  inverter_outage_monthly: "Inverter outage",
  meter_energy_divergence: "Meter divergence",
  inverter_hot: "Inverter running hot",
  manual_unavailability: "Manual",
  event_log: "Event log",
};

const CATEGORY_LABELS: Record<string, string> = {
  connectivity: "Connectivity",
  device_state: "Device State",
  data_quality: "Data Quality",
  anomaly: "Anomaly",
  event_log: "Event log",
};

const IMPACT_OPTIONS = [
  "unplanned_pv_outage",
  "unplanned_pv_outage_excused",
  "unplanned_battery_outage",
  "unplanned_battery_outage_excused",
  "metering_discrepancy",
  "import_limit_breach",
  "equipment_alert",
  "transient",
  "planned",
  "ignore",
] as const;

const IMPACT_LABELS: Record<string, string> = {
  unplanned_pv_outage: "PV outage",
  unplanned_pv_outage_excused: "PV outage (excused)",
  unplanned_battery_outage: "BESS outage",
  unplanned_battery_outage_excused: "BESS outage (excused)",
  metering_discrepancy: "Metering discrepancy",
  import_limit_breach: "Import limit breach",
  equipment_alert: "Equipment alert",
  transient: "Transient",
  planned: "Planned",
  ignore: "Ignore",
};

// The excused (Excludable Event) variants; selecting one requires a reason and
// removes the outage from both the numerator and denominator of availability.
const EXCUSED_IMPACTS = new Set([
  "unplanned_pv_outage_excused",
  "unplanned_battery_outage_excused",
]);

const formatDuration = (start: string, end: string | null): string => {
  if (!end) return "Ongoing";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const todayInTimeZone = (timeZone: string): string => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")!.value;
  const m = parts.find((p) => p.type === "month")!.value;
  const d = parts.find((p) => p.type === "day")!.value;
  return `${y}-${m}-${d}`;
};

const readingsEndDate = (
  event: { resolvedAt: string | null; detectedAt: string },
  timeZone: string
): string =>
  event.resolvedAt ? event.resolvedAt.slice(0, 10) : todayInTimeZone(timeZone);

const DATE_FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "30", label: "Last 30 days" },
  { key: "365", label: "Last 365 days" },
] as const;

// "all" | "30" | "365" | a YYYY-MM string for a specific complete month.
type DateFilter = string;

const isMonthFilter = (filter: DateFilter): boolean =>
  /^\d{4}-\d{2}$/.test(filter);

/** Inclusive UTC ms range [start, endExclusive) for the given YYYY-MM in site TZ. */
const monthFilterRangeMs = (
  filter: string,
  timeZone: string
): { startMs: number; endMs: number } => {
  const [year, month] = filter.split("-").map(Number);
  // Compute the UTC instant corresponding to local-midnight on the 1st of
  // the target month and 1st of the next month, in `timeZone`. Using a
  // notional UTC date and reading what hour it lands on in the site tz
  // gives the offset to apply.
  const offsetMs = (y: number, m: number) => {
    const utc = Date.UTC(y, m - 1, 1, 0, 0, 0);
    const localStr = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(utc));
    // localStr is "MM/DD/YYYY, HH:MM" — parse it and treat as if-that-were-UTC
    const m2 = localStr.match(/(\d{2})\/(\d{2})\/(\d{4}),\s*(\d{2}):(\d{2})/);
    if (!m2) return 0;
    const asUtc = Date.UTC(
      Number(m2[3]),
      Number(m2[1]) - 1,
      Number(m2[2]),
      Number(m2[4]) % 24,
      Number(m2[5])
    );
    return utc - asUtc;
  };
  const startUtc = Date.UTC(year, month - 1, 1, 0, 0, 0);
  const endUtc = Date.UTC(year, month, 1, 0, 0, 0);
  return {
    startMs: startUtc + offsetMs(year, month),
    endMs: endUtc + offsetMs(year, month + 1 > 12 ? 1 : month + 1),
  };
};

const EditNotesModal = ({
  show,
  onHide,
  initialValue,
  onSave,
}: {
  show: boolean;
  onHide: () => void;
  initialValue: string;
  onSave: (notes: string) => void;
}) => {
  const [value, setValue] = useState(initialValue);

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Notes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          as="textarea"
          rows={4}
          value={value}
          placeholder="Add notes..."
          onChange={(e) => setValue(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSave(value.trim());
            onHide();
          }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EditManualEventModal = ({
  show,
  onHide,
  event,
  timeZone,
  onSave,
  isSubmitting,
}: {
  show: boolean;
  onHide: () => void;
  event: HealthEvent;
  timeZone: string;
  onSave: (update: {
    category: string;
    severity: string;
    impactType: string;
    detectedAt: string;
    resolvedAt: string | null;
    notes: string;
  }) => void;
  isSubmitting: boolean;
}) => {
  const [category, setCategory] = useState(event.category);
  const [severity, setSeverity] = useState(event.severity);
  const [impactType, setImpactType] = useState(event.impactType);
  const [notes, setNotes] = useState(event.notes ?? "");
  const [startTime, setStartTime] = useState(
    utcIsoToSiteLocalDatetimeValue(event.detectedAt, timeZone)
  );
  const [endTime, setEndTime] = useState(
    event.resolvedAt
      ? utcIsoToSiteLocalDatetimeValue(event.resolvedAt, timeZone)
      : ""
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Manual Event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Category</Form.Label>
          <Form.Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Severity</Form.Label>
          <Form.Select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="red">Red</option>
            <option value="amber">Amber</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Impact Type</Form.Label>
          <Form.Select
            value={impactType}
            onChange={(e) => setImpactType(e.target.value)}
          >
            {IMPACT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {IMPACT_LABELS[opt]}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Start Time ({timeZone})</Form.Label>
          <Form.Control
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>End Time, optional ({timeZone})</Form.Label>
          <Form.Control
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSave({
              category,
              severity,
              impactType,
              detectedAt: siteLocalDatetimeValueToUtcIso(startTime, timeZone),
              resolvedAt: endTime
                ? siteLocalDatetimeValueToUtcIso(endTime, timeZone)
                : null,
              notes: notes.trim(),
            });
            onHide();
          }}
          disabled={isSubmitting || !startTime}
        >
          {isSubmitting ? <Spinner animation="border" size="sm" /> : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const HealthEventsTable = ({
  events,
  siteId,
  timeZone,
  esysEntries,
  onUpdateImpactType,
  onUpdateNotes,
  onUpdateExclusionReason,
  onUpdateEvent,
  onDeleteEvent,
  isUpdating,
  title,
  showDateFilter = true,
  defaultStatusFilter = "all",
  showSiteColumn = false,
  siteNameByEvent,
  showDeleteColumn = true,
  readOnly = false,
  earliestMonthInclusive,
  dataRangeNotice,
  dateFilterControls,
  bodyOverride,
}: {
  events: HealthEvent[];
  siteId: string;
  timeZone: string;
  esysEntries: [string, SiteElectricalSystem][];
  onUpdateImpactType?: (
    eventId: number,
    impactType: string,
    exclusionReason?: string
  ) => void;
  onUpdateNotes?: (eventId: number, notes: string) => void;
  onUpdateExclusionReason?: (eventId: number, exclusionReason: string) => void;
  onUpdateEvent?: (
    eventId: number,
    update: {
      category: string;
      severity: string;
      impactType: string;
      detectedAt: string;
      resolvedAt: string | null;
      notes: string;
    }
  ) => void;
  onDeleteEvent?: (eventId: number) => void;
  isUpdating?: boolean;
  title?: string;
  showDateFilter?: boolean;
  defaultStatusFilter?:
    | "all"
    | "open"
    | "openAndNonTransientResolved"
    | "retracted";
  showSiteColumn?: boolean;
  siteNameByEvent?: Record<string, string>;
  showDeleteColumn?: boolean;
  readOnly?: boolean;
  /** YYYY-MM cutoff (inclusive) — months before this are dropped from the
   *  "Specific month" dropdown. Use the site's COD on single-site pages so
   *  the dropdown doesn't list pre-operational months. */
  earliestMonthInclusive?: string | null;
  /** Optional one-line notice rendered immediately under the table title
   *  describing what time range the upstream data covers. Use this when
   *  the caller has already date-filtered the event set server-side
   *  (e.g. /operational/health passes "open + resolved-since-X") - the
   *  in-table date-filter buttons can only narrow further, so an explicit
   *  notice prevents operators from assuming they're seeing every event
   *  ever recorded. */
  dataRangeNotice?: string | null;
  /** Custom date-filter controls rendered in the card header (where the
   *  built-in `showDateFilter` block sits). Cross-site surfaces like
   *  /operational/health pass their own BE-backed period selector here so it
   *  appears inside the card, matching the per-site page. Pair with
   *  `showDateFilter={false}` to replace the built-in client-side filter. */
  dateFilterControls?: React.ReactNode;
  /** When set, replaces the table body (rows / "No events found"). Used to show
   *  a loading or error state inside the card while keeping the header + filter
   *  controls visible during a server-side refetch. */
  bodyOverride?: React.ReactNode;
}) => {
  const esysByUuid = Object.fromEntries(
    esysEntries.map(([, esys]) => [esys.uuid, esys])
  );
  const esysCount = esysEntries.length;
  const [statusFilter, setStatusFilter] = useState<
    "all" | "open" | "openAndNonTransientResolved" | "retracted"
  >(defaultStatusFilter);
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [expandedSpans, setExpandedSpans] = useState<Set<number>>(new Set());
  const [editingManualEventId, setEditingManualEventId] = useState<
    number | null
  >(null);
  const [editingNotesEventId, setEditingNotesEventId] = useState<number | null>(
    null
  );

  // Cross-site tables (e.g. /operational/health) pass siteId="" and rely on
  // each event carrying its own siteId, so the readings link must be built
  // from the per-event site rather than the table-level prop.
  const readingsUrl = (
    eventSiteId: string,
    startDate: string,
    endDate: string,
    esysUuid?: string | null
  ) => {
    const linkSiteId = eventSiteId || siteId;
    let url = `/site/${linkSiteId}/perf/readings?dataMode=processed&startDate=${startDate}&endDate=${endDate}&preset=availability`;
    if (esysCount > 1 && esysUuid && esysByUuid[esysUuid]) {
      url += `&esys=${esysByUuid[esysUuid].idx}`;
    }
    return url;
  };

  const monthFilterOptions = useMemo(
    () => buildMonthFilterOptions(new Date(), timeZone, earliestMonthInclusive),
    [timeZone, earliestMonthInclusive]
  );

  // One place for the status predicates so the row filter and per-button counts
  // can't drift. "retracted" = resolved rows carrying `retracted_spans`.
  const isTransient = (e: HealthEvent) => e.impactType === "transient";
  const isRetracted = (e: HealthEvent) => {
    if (!e.resolvedAt || !e.details) return false;
    const spans = (e.details as { retracted_spans?: unknown }).retracted_spans;
    return Array.isArray(spans) && spans.length > 0;
  };
  const matchesStatus = (
    e: HealthEvent,
    key: "all" | "open" | "openAndNonTransientResolved" | "retracted"
  ): boolean => {
    if (key === "all") return true;
    if (key === "open") return !e.resolvedAt;
    if (key === "retracted") return isRetracted(e);
    // openAndNonTransientResolved: include unless the event is both
    // resolved and transient.
    return !(e.resolvedAt && isTransient(e));
  };

  // Apply the date filter once, then layer the status filter on top. The
  // date-filtered list is also the basis for the per-button counts so they
  // respect the date range and only vary with the chosen status.
  const dateFiltered = events.filter((e) => {
    if (dateFilter !== "all") {
      if (isMonthFilter(dateFilter)) {
        const { startMs, endMs } = monthFilterRangeMs(dateFilter, timeZone);
        const detectedMs = new Date(e.detectedAt).getTime();
        if (detectedMs < startMs || detectedMs >= endMs) return false;
      } else {
        const days = parseInt(dateFilter);
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        if (new Date(e.detectedAt) < cutoff) return false;
      }
    }
    return true;
  });

  const typeCounts: Record<string, number> = {};
  for (const e of dateFiltered) {
    typeCounts[e.checkType] = (typeCounts[e.checkType] ?? 0) + 1;
  }
  const typeLabel = (checkType: string) =>
    CHECK_TYPE_LABELS[checkType] ?? checkType;
  const typeOptions = Object.keys(typeCounts).sort((a, b) =>
    typeLabel(a).localeCompare(typeLabel(b))
  );
  const typeFiltered = typeFilter
    ? dateFiltered.filter((e) => e.checkType === typeFilter)
    : dateFiltered;

  const filtered = typeFiltered.filter((e) => matchesStatus(e, statusFilter));

  // Cross-site tables (showSiteColumn) sort by site name asc, then detected_at
  // desc, so the table reads like the per-site /site/<id>/health/status pages
  // appended together. This mirrors the daily digest's alphabetical-by-name
  // (case-insensitive, site.id tie-break) convention. Single-site tables keep
  // the upstream order (detected_at desc).
  const displayRows = showSiteColumn
    ? [...filtered].sort((a, b) => {
        const nameA = (
          siteNameByEvent?.[String(a.id)] ?? a.siteId
        ).toLowerCase();
        const nameB = (
          siteNameByEvent?.[String(b.id)] ?? b.siteId
        ).toLowerCase();
        if (nameA !== nameB) return nameA < nameB ? -1 : 1;
        if (a.siteId !== b.siteId) return a.siteId < b.siteId ? -1 : 1;
        return (
          new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
        );
      })
    : filtered;

  // Per-button counts: how many rows would each filter show, given the
  // currently-selected date range. Pre-computed so each button can render a
  // count badge without re-filtering on every render.
  const STATUS_FILTER_BUTTONS = [
    { key: "all", label: "All" },
    { key: "open", label: "Open" },
    {
      key: "openAndNonTransientResolved",
      label: "Open + Non-transient resolved",
    },
    { key: "retracted", label: "Retracted" },
  ] as const;
  const statusCounts: Record<string, number> = {};
  for (const f of STATUS_FILTER_BUTTONS) {
    statusCounts[f.key] = typeFiltered.filter((e) =>
      matchesStatus(e, f.key)
    ).length;
  }

  const editingEvent = events.find((e) => e.id === editingNotesEventId);
  const editingManualEvent = events.find((e) => e.id === editingManualEventId);

  return (
    <Card body>
      <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
        <div>
          <h6 className="fw-bold mb-0">
            {title ?? "Health Events"} (
            {filtered.length === events.length
              ? events.length
              : `${filtered.length} of ${events.length}`}
            )
          </h6>
          {dataRangeNotice && (
            <div className="text-muted small mt-1">{dataRangeNotice}</div>
          )}
        </div>
        {!readOnly && (
          <div className="d-flex gap-3 flex-wrap">
            {dateFilterControls}
            {showDateFilter && (
              <div className="d-flex gap-1 align-items-center flex-wrap">
                {DATE_FILTER_OPTIONS.map((opt) => (
                  <Button
                    key={opt.key}
                    size="sm"
                    variant={
                      dateFilter === opt.key ? "dark" : "outline-secondary"
                    }
                    onClick={() => setDateFilter(opt.key)}
                  >
                    {opt.label}
                  </Button>
                ))}
                <Form.Select
                  size="sm"
                  style={{ width: "auto" }}
                  value={isMonthFilter(dateFilter) ? dateFilter : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) setDateFilter(v);
                    else setDateFilter("all");
                  }}
                >
                  <option value="">Specific month…</option>
                  {monthFilterOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            )}
            <Form.Select
              size="sm"
              style={{ width: "auto" }}
              aria-label="Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              {typeFilter && !typeCounts[typeFilter] && (
                <option value={typeFilter}>{typeLabel(typeFilter)} (0)</option>
              )}
              {typeOptions.map((checkType) => (
                <option key={checkType} value={checkType}>
                  {typeLabel(checkType)} ({typeCounts[checkType]})
                </option>
              ))}
            </Form.Select>
            <div className="d-flex gap-1 flex-wrap">
              {STATUS_FILTER_BUTTONS.map((f) => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={
                    statusFilter === f.key ? "primary" : "outline-secondary"
                  }
                  onClick={() => setStatusFilter(f.key)}
                >
                  {f.label} ({statusCounts[f.key]})
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {bodyOverride !== undefined ? (
        bodyOverride
      ) : filtered.length === 0 ? (
        <div className="text-muted py-3 text-center">No events found</div>
      ) : (
        <Table striped hover size="sm" responsive>
          <thead>
            <tr>
              <th>Status</th>
              {showSiteColumn && <th>Site</th>}
              <th>Category</th>
              <th>Type</th>
              <th>Severity</th>
              {esysCount > 1 && <th>Energy match</th>}
              <th>Detected</th>
              <th>Resolved</th>
              <th>Duration</th>
              {!readOnly && <th>Impact</th>}
              <th>Summary</th>
              {!readOnly && <th>Actions</th>}
              {!readOnly && showDeleteColumn && <th></th>}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((event) => (
              <tr key={event.id}>
                <td>
                  <Badge bg={event.resolvedAt ? "secondary" : "danger"}>
                    {event.resolvedAt ? "Resolved" : "Open"}
                  </Badge>
                </td>
                {showSiteColumn && (
                  <td className="small">
                    <Link to={`/site/${event.siteId}/health/status`}>
                      {siteNameByEvent?.[String(event.id)] ?? event.siteId}
                    </Link>
                  </td>
                )}
                <td className="small">
                  {CATEGORY_LABELS[event.category] ?? event.category}
                </td>
                <td className="small">
                  {CHECK_TYPE_LABELS[event.checkType] ?? event.checkType}
                </td>
                <td>
                  <Badge bg={SEVERITY_COLORS[event.severity] || "secondary"}>
                    {event.severity}
                  </Badge>
                </td>
                {esysCount > 1 && (
                  <td className="small">
                    {event.esysUuid && esysByUuid[event.esysUuid]
                      ? getESysName(
                          esysCount,
                          esysByUuid[event.esysUuid]
                        ).trim()
                      : "Site"}
                  </td>
                )}
                <td className="small">
                  {formatSiteDateTime(event.detectedAt, timeZone)}
                </td>
                <td className="small">
                  {event.resolvedAt
                    ? formatSiteDateTime(event.resolvedAt, timeZone)
                    : "-"}
                </td>
                <td className="small">
                  {formatDuration(event.detectedAt, event.resolvedAt)}
                </td>
                {!readOnly && (
                  <td>
                    <Form.Select
                      size="sm"
                      value={event.impactType}
                      disabled={isUpdating}
                      onChange={(e) => {
                        const val = e.target.value;
                        // Selecting an excused variant requires a reason (unless the
                        // event already carries one); it removes the outage from both
                        // the numerator and denominator of availability.
                        if (
                          EXCUSED_IMPACTS.has(val) &&
                          !event.exclusionReason
                        ) {
                          const reason = window.prompt(
                            "Reason for excusing this outage (which Excludable Event — grid / FM / Free-Issue / …)?"
                          );
                          if (!reason || !reason.trim()) return;
                          onUpdateImpactType?.(event.id, val, reason.trim());
                        } else {
                          onUpdateImpactType?.(event.id, val);
                        }
                      }}
                      style={{ width: "auto", minWidth: "110px" }}
                    >
                      {IMPACT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {IMPACT_LABELS[opt]}
                        </option>
                      ))}
                    </Form.Select>
                    {EXCUSED_IMPACTS.has(event.impactType) && (
                      <div
                        className="mt-1 small text-muted"
                        title={event.exclusionReason ?? undefined}
                      >
                        {event.exclusionReason &&
                          `Excused: ${event.exclusionReason} `}
                        {onUpdateExclusionReason && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 align-baseline small"
                            disabled={isUpdating}
                            onClick={() => {
                              const reason = window.prompt(
                                "Reason for excusing this outage (which Excludable Event - grid / FM / Free-Issue / ...)?",
                                event.exclusionReason ?? ""
                              );
                              if (
                                reason &&
                                reason.trim() &&
                                reason.trim() !== event.exclusionReason
                              ) {
                                onUpdateExclusionReason(
                                  event.id,
                                  reason.trim()
                                );
                              }
                            }}
                          >
                            {event.exclusionReason
                              ? "Edit reason"
                              : "Add reason"}
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                )}
                <td className="small">
                  <div>
                    {event.summary}
                    {event.checkType === "inverter_outage_monthly" && (
                      <InverterOutageSpans
                        event={event}
                        expandedSpans={expandedSpans}
                        setExpandedSpans={setExpandedSpans}
                        readingsUrl={readingsUrl}
                        timeZone={timeZone}
                      />
                    )}
                    {event.checkType !== "inverter_outage_monthly" &&
                      !event.notes && (
                        <>
                          {" "}
                          (
                          <Link
                            to={readingsUrl(
                              event.siteId,
                              event.detectedAt.slice(0, 10),
                              readingsEndDate(event, timeZone),
                              event.esysUuid
                            )}
                          >
                            Readings
                          </Link>
                          )
                        </>
                      )}
                  </div>
                  {event.checkType === "solaredge_alert" && (
                    <SolarEdgeAlertDetails
                      event={event}
                      timeZone={timeZone}
                      canAcknowledge={
                        !readOnly && siteId !== "" && event.resolvedAt === null
                      }
                    />
                  )}
                  {event.notes && (
                    <div className="mt-1">
                      <strong>Note: </strong>
                      {stringToReactNodesWithLinks(event.notes)}
                      {event.checkType !== "inverter_outage_monthly" && (
                        <>
                          {" "}
                          (
                          <Link
                            to={readingsUrl(
                              event.siteId,
                              event.detectedAt.slice(0, 10),
                              readingsEndDate(event, timeZone),
                              event.esysUuid
                            )}
                          >
                            Readings
                          </Link>
                          )
                        </>
                      )}
                    </div>
                  )}
                </td>
                {!readOnly && (
                  <td>
                    {event.source === "manual" && onUpdateEvent ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                        onClick={() => setEditingManualEventId(event.id)}
                      >
                        Edit event
                      </Button>
                    ) : (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                        onClick={() => setEditingNotesEventId(event.id)}
                      >
                        {event.notes ? "Edit note" : "Add note"}
                      </Button>
                    )}
                  </td>
                )}
                {!readOnly && showDeleteColumn && (
                  <td>
                    {event.source === "manual" && (
                      <img
                        alt="Delete"
                        src={Icons.trashcanRed}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                        onClick={() => onDeleteEvent?.(event.id)}
                        title="Delete manual event"
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {editingEvent && onUpdateNotes && (
        <EditNotesModal
          show={editingNotesEventId !== null}
          onHide={() => setEditingNotesEventId(null)}
          initialValue={editingEvent.notes ?? ""}
          onSave={(notes) => onUpdateNotes(editingEvent.id, notes)}
        />
      )}
      {editingManualEvent && onUpdateEvent && (
        <EditManualEventModal
          show={editingManualEventId !== null}
          onHide={() => setEditingManualEventId(null)}
          event={editingManualEvent}
          timeZone={timeZone}
          onSave={(update) => onUpdateEvent(editingManualEvent.id, update)}
          isSubmitting={isUpdating ?? false}
        />
      )}
    </Card>
  );
};

const FieldTooltip = ({
  content,
  children,
}: {
  content: React.ReactNode;
  children: React.ReactElement;
}) => (
  <WithTooltip placement="right" text={content} className="wide-tooltip">
    {children}
  </WithTooltip>
);

const CreateManualEventModal = ({
  show,
  onHide,
  timeZone,
  onSubmit,
  isSubmitting,
}: {
  show: boolean;
  onHide: () => void;
  timeZone: string;
  onSubmit: (data: {
    category: string;
    checkType: string;
    severity: string;
    detectedAt: string;
    resolvedAt?: string;
    impactType: string;
    summary: string;
    notes?: string;
  }) => void;
  isSubmitting: boolean;
}) => {
  const [category, setCategory] = useState("event_log");
  const [severity, setSeverity] = useState("red");
  const [impactType, setImpactType] = useState("planned");
  const [notes, setNotes] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const handleSubmit = () => {
    onSubmit({
      category,
      checkType: "manual_unavailability",
      severity,
      detectedAt: siteLocalDatetimeValueToUtcIso(startTime, timeZone),
      resolvedAt: endTime
        ? siteLocalDatetimeValueToUtcIso(endTime, timeZone)
        : undefined,
      impactType,
      summary: "",
      notes: notes || undefined,
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Manual Event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <FieldTooltip
            content={
              <>
                Classifies the type of event:
                <br />
                <strong>Connectivity</strong> = data pipeline issues
                <br />
                <strong>Data Quality</strong> = anomalous meter readings
                <br />
                <strong>Device State</strong> = hardware fault indicators
                <br />
                <strong>Anomaly</strong> = unexpected patterns
                <br />
                <strong>Event log</strong> = manual annotation of site events
              </>
            }
          >
            <Form.Label style={{ cursor: "help" }}>
              Category <span className="text-muted">(?)</span>
            </Form.Label>
          </FieldTooltip>
          <Form.Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <FieldTooltip
            content={
              <>
                <strong>Red</strong> = critical issue requiring immediate
                attention
                <br />
                <strong>Amber</strong> = warning that should be investigated
              </>
            }
          >
            <Form.Label style={{ cursor: "help" }}>
              Severity <span className="text-muted">(?)</span>
            </Form.Label>
          </FieldTooltip>
          <Form.Select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="red">Red</option>
            <option value="amber">Amber</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <FieldTooltip
            content={
              <>
                <strong>Unplanned PV outage</strong> = genuine PV fault, counts
                against PV availability
                <br />
                <strong>Unplanned battery outage</strong> = genuine battery
                fault, counts against battery availability
                <br />
                <strong>
                  Metering discrepancy / Import limit breach / Equipment alert
                </strong>{" "}
                = health issue to investigate, does not affect availability
                <br />
                <strong>Planned</strong> = scheduled maintenance, excluded from
                availability
                <br />
                <strong>Transient</strong> = short-lived issue
                <br />
                <strong>Ignore</strong> = known issue excluded from reporting
              </>
            }
          >
            <Form.Label style={{ cursor: "help" }}>
              Impact Type <span className="text-muted">(?)</span>
            </Form.Label>
          </FieldTooltip>
          <Form.Select
            value={impactType}
            onChange={(e) => setImpactType(e.target.value)}
          >
            {IMPACT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {IMPACT_LABELS[opt]}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Start Time ({timeZone})</Form.Label>
          <Form.Control
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>End Time, optional ({timeZone})</Form.Label>
          <Form.Control
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Scheduled maintenance window"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !startTime}
        >
          {isSubmitting ? <Spinner animation="border" size="sm" /> : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const SimConnectivityCard = ({
  siteId,
  siteName,
  esysWithSim,
  esysCount,
  isLoading,
  simStatusByEsys,
}: {
  siteId: string;
  siteName: string;
  esysWithSim: [string, SiteElectricalSystem][];
  esysCount: number;
  isLoading: boolean;
  simStatusByEsys: ReturnType<typeof useSiteSetupActions>["simStatusByEsys"];
}) => {
  // Sites with no Soracom SIM must not trigger a ping query; the hook is still
  // called every render (rules of hooks), so `enabled` is the off switch.
  const pingLatestQuery = useSitePingLatest(siteId, esysWithSim.length > 0);
  const latestByEsys = pingLatestQuery.data?.latestByEsys ?? {};

  // Which esys's ping-history modal is open (null when closed).
  const [pingHistoryEsys, setPingHistoryEsys] =
    useState<SiteElectricalSystem | null>(null);

  if (isLoading) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">Site internet connection</h6>
        <Spinner animation="border" size="sm" />
      </Card>
    );
  }

  if (esysWithSim.length === 0) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">Site internet connection</h6>
        <div className="text-muted small">No SIM registered.</div>
      </Card>
    );
  }

  return (
    <>
      <Card body>
        <h6 className="fw-bold mb-2">Site internet connection</h6>
        <div className="d-flex flex-column gap-3">
          {esysWithSim.map(([key, esys]) => {
            const simStatusQuery = simStatusByEsys[esys.uuid];
            const simStatus = simStatusQuery?.data?.sim_status;
            const esysLabel =
              esysCount > 1
                ? `Energy match${getESysName(esysCount, esys)}`
                : null;
            const pingLatest = latestByEsys[esys.uuid] ?? null;
            const headline = computeConnectivityHeadline(
              simStatusQuery?.isLoading ?? false,
              simStatus,
              pingLatest
            );
            return (
              <div
                key={key}
                className="d-flex flex-column gap-1"
                data-testid="sim-connectivity-row"
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {esysLabel && (
                    <span className="fw-semibold">{esysLabel}:</span>
                  )}
                  <Badge bg={headline.variant}>{headline.label}</Badge>
                </div>
                {pingLatest?.networkName && (
                  <div className="text-muted small">
                    Network: {pingLatest.networkName}
                  </div>
                )}
                <div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setPingHistoryEsys(esys)}
                  >
                    View ping history
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      {pingHistoryEsys !== null && (
        <SitePingHistoryModal
          show={true}
          onHide={() => setPingHistoryEsys(null)}
          siteId={siteId}
          siteName={siteName}
          esysUuid={pingHistoryEsys.uuid}
          esysLabel={
            esysCount > 1
              ? `Energy match${getESysName(esysCount, pingHistoryEsys)}`
              : null
          }
        />
      )}
    </>
  );
};

type ConnectivityHeadline = {
  label: string;
  variant: "success" | "warning" | "danger" | "secondary";
};

/**
 * Combine the SIM session-status signal (passive — "Soracom thinks the
 * SIM is authenticated to the network") with the most-recent Soracom
 * downlink ping result (active — "we just got an ICMP reply") into
 * a single tile headline. The Wolverhampton incident showed that
 * session-status can stay "Online" for hours while the modem isn't
 * actually responding — never say "Online" when packet loss is 100%.
 */
const computeConnectivityHeadline = (
  simStatusLoading: boolean,
  simStatus: { online: boolean; ue_ip_address?: string | null } | undefined,
  pingLatest: { packetLossPct: number; checkTimestamp: string } | null
): ConnectivityHeadline => {
  if (simStatusLoading) {
    return { label: "Checking…", variant: "secondary" };
  }
  if (!simStatus) {
    return { label: "Connection status unknown", variant: "secondary" };
  }
  if (!simStatus.online) {
    return { label: "Offline", variant: "danger" };
  }
  // Session shows online — refine with the ping result.
  if (!pingLatest || !isPingRecent(pingLatest.checkTimestamp)) {
    return { label: "Online, ping check pending", variant: "secondary" };
  }
  if (pingLatest.packetLossPct === 0) {
    return { label: "Online, responding to pings", variant: "success" };
  }
  if (pingLatest.packetLossPct >= 100) {
    return {
      label: "Online, but not responding to pings",
      variant: "danger",
    };
  }
  return {
    label: `Online, ${pingLatest.packetLossPct.toFixed(0)}% ping packet loss`,
    variant: "warning",
  };
};

const PING_FRESHNESS_MINUTES = 30;

const isPingRecent = (checkTimestamp: string): boolean => {
  const ageMs = Date.now() - new Date(checkTimestamp).getTime();
  return ageMs < PING_FRESHNESS_MINUTES * 60_000;
};

const formatCoverageDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const WeatherActualsCoverageCard = ({
  coverage,
  isLoading,
  noActualsExplanation,
}: {
  coverage: WeatherActualsCoverageResponse | undefined;
  isLoading: boolean;
  noActualsExplanation: string;
}) => {
  if (isLoading) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">Weather actuals</h6>
        <Spinner animation="border" size="sm" />
      </Card>
    );
  }

  if (!coverage?.orientations?.length) {
    return (
      <Card body>
        <h6 className="fw-bold mb-2">Weather actuals</h6>
        <div className="text-muted small">{noActualsExplanation}</div>
      </Card>
    );
  }

  return (
    <Card body>
      <h6 className="fw-bold mb-2">Weather actuals</h6>
      {coverage.orientations.map((o, i) => {
        const ranges = o.covered_ranges;
        const gaps = o.gaps;
        const start = ranges[0]?.start;
        const end = ranges[ranges.length - 1]?.end;
        const gapCount = gaps.length;
        return (
          <div key={i} className="text-muted small">
            <div>
              Tilt {o.tilt}° / Azimuth {o.azimuth}°:
            </div>
            <div>
              {start && end
                ? `${formatCoverageDate(start)} — ${formatCoverageDate(end)} (${gapCount === 0 ? "no gaps" : `${gapCount} gap${gapCount > 1 ? "s" : ""}`})`
                : "No data"}
            </div>
            {gaps.map((g, gi) => (
              <div key={gi} className="ms-3">
                Gap: {formatCoverageDate(g.start)} — {formatCoverageDate(g.end)}
              </div>
            ))}
          </div>
        );
      })}
    </Card>
  );
};

const SiteHealthStatus = ({
  site,
  isDemo,
  esysEntries,
  ingestionByEsys,
  simStatusByEsys,
}: {
  site: Site;
  isDemo: boolean;
  esysEntries: [string, SiteElectricalSystem][];
  ingestionByEsys: ReturnType<typeof useSiteSetupActions>["ingestionByEsys"];
  simStatusByEsys: ReturnType<typeof useSiteSetupActions>["simStatusByEsys"];
}) => {
  const { healthEvents, updateEvent, createManualEvent, deleteEvent } =
    useSiteHealthEvents(site.id);
  const { coverageQuery } = useWeatherActuals({ siteId: site.id });
  const { prQuery } = useSitePR(site.id);
  const { healthConfig } = useSiteHealthConfig(site.id);
  // Demo sites have no health-config row but are implicitly weather-enabled
  // server-side (they share their source site's weather data). While the config
  // is still loading, fall back to the neutral message rather than asserting an
  // ingestion state we don't yet know.
  const noActualsExplanation = healthConfig.isLoading
    ? NO_WEATHER_ACTUALS_FALLBACK
    : weatherActualsNoDataExplanation(
        siteHasPanelOrientations(site),
        isDemo || (healthConfig.data?.weatherActualsEnabled ?? false)
      );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExplainAvailability, setShowExplainAvailability] = useState(false);
  const [showExplainBess, setShowExplainBess] = useState(false);
  const [showExplainPR, setShowExplainPR] = useState(false);
  const [showExplainOPR, setShowExplainOPR] = useState(false);

  const esysWithSim = esysEntries.filter(
    ([, esys]) => !!ingestionByEsys[esys.uuid]?.data?.site?.soracom_imsi
  );

  const handleUpdateImpactType = (
    eventId: number,
    impactType: string,
    exclusionReason?: string
  ) => {
    updateEvent.mutate(
      {
        eventId,
        impactType,
        ...(exclusionReason !== undefined ? { exclusionReason } : {}),
      },
      {
        onError: (err) => {
          alert(
            `Failed to update: ${err instanceof Error ? err.message : "Unknown error"}`
          );
        },
      }
    );
  };

  const handleUpdateExclusionReason = (
    eventId: number,
    exclusionReason: string
  ) => {
    updateEvent.mutate(
      { eventId, exclusionReason },
      {
        onError: (err) => {
          alert(
            `Failed to save reason: ${err instanceof Error ? err.message : "Unknown error"}`
          );
        },
      }
    );
  };

  const handleUpdateNotes = (eventId: number, notes: string) => {
    updateEvent.mutate(
      { eventId, notes },
      {
        onError: (err) => {
          alert(
            `Failed to save notes: ${err instanceof Error ? err.message : "Unknown error"}`
          );
        },
      }
    );
  };

  const handleUpdateEvent = (
    eventId: number,
    update: {
      category: string;
      severity: string;
      impactType: string;
      detectedAt: string;
      resolvedAt: string | null;
      notes: string;
    }
  ) => {
    updateEvent.mutate(
      {
        eventId,
        category: update.category,
        severity: update.severity,
        impactType: update.impactType,
        detectedAt: update.detectedAt,
        resolvedAt: update.resolvedAt,
        notes: update.notes,
      },
      {
        onError: (err) => {
          alert(
            `Failed to update event: ${err instanceof Error ? err.message : "Unknown error"}`
          );
        },
      }
    );
  };

  const handleDeleteEvent = (eventId: number) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    deleteEvent.mutate(eventId, {
      onError: (err) => {
        alert(
          `Failed to delete: ${err instanceof Error ? err.message : "Unknown error"}`
        );
      },
    });
  };

  const handleCreateManualEvent = (data: {
    category: string;
    checkType: string;
    severity: string;
    detectedAt: string;
    resolvedAt?: string;
    impactType: string;
    summary: string;
    notes?: string;
  }) => {
    createManualEvent.mutate(data, {
      onSuccess: () => setShowCreateModal(false),
      onError: (err) => {
        alert(
          `Failed to create event: ${err instanceof Error ? err.message : "Unknown error"}`
        );
      },
    });
  };

  return (
    <Container
      fluid
      style={{ padding: "12px 12px 2rem 12px" }}
      className="d-flex flex-column gap-3"
    >
      {isDemo ? (
        <div className="text-muted">
          Demo site — health monitoring not available.
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "1rem",
            }}
          >
            <AvailabilityCard
              siteId={site.id}
              availability={healthEvents.data?.availability}
              isLoading={healthEvents.isLoading}
              onExplain={() => setShowExplainAvailability(true)}
            />
            {healthEvents.data?.bessAvailability && (
              <AvailabilityCard
                siteId={site.id}
                availability={healthEvents.data.bessAvailability}
                isLoading={healthEvents.isLoading}
                onExplain={() => setShowExplainBess(true)}
                title="BESS Availability"
              />
            )}
            <GPRCard
              siteId={site.id}
              prData={prQuery.data}
              isLoading={prQuery.isLoading}
              onExplain={() => setShowExplainPR(true)}
              noActualsExplanation={noActualsExplanation}
            />
            <OPRCard
              siteId={site.id}
              prData={prQuery.data}
              isLoading={prQuery.isLoading}
              onExplain={() => setShowExplainOPR(true)}
              noActualsExplanation={noActualsExplanation}
            />
            <GPRCard
              siteId={site.id}
              prData={prQuery.data}
              isLoading={prQuery.isLoading}
              onExplain={() => setShowExplainPR(true)}
              noActualsExplanation={noActualsExplanation}
              deemed
            />
            <OPRCard
              siteId={site.id}
              prData={prQuery.data}
              isLoading={prQuery.isLoading}
              onExplain={() => setShowExplainOPR(true)}
              noActualsExplanation={noActualsExplanation}
              deemed
            />

            <SimConnectivityCard
              siteId={site.id}
              siteName={site.name}
              esysWithSim={esysWithSim}
              esysCount={esysEntries.length}
              isLoading={esysEntries.some(
                ([, esys]) => ingestionByEsys[esys.uuid]?.isLoading
              )}
              simStatusByEsys={simStatusByEsys}
            />
            <MonitoringPortalsCard
              esysEntries={esysEntries}
              ingestionByEsys={ingestionByEsys}
              alwaysRender
            />
            <WeatherActualsCoverageCard
              coverage={coverageQuery.data}
              isLoading={coverageQuery.isLoading}
              noActualsExplanation={noActualsExplanation}
            />
            <HealthInvestigationCard
              siteId={site.id}
              siteName={site.name}
              timeZone={site.timeZone}
            />
          </div>
          <AvailabilityExplainModal
            show={showExplainAvailability}
            onHide={() => setShowExplainAvailability(false)}
          />
          <BessAvailabilityExplainModal
            show={showExplainBess}
            onHide={() => setShowExplainBess(false)}
          />
          <PRExplainModal
            show={showExplainPR}
            onHide={() => setShowExplainPR(false)}
          />
          <OPRExplainModal
            show={showExplainOPR}
            onHide={() => setShowExplainOPR(false)}
          />

          <div className="d-flex justify-content-between align-items-center">
            <div>
              {healthEvents.data?.openEventCount && (
                <span className="text-muted small">
                  Open events:{" "}
                  {Object.entries(healthEvents.data.openEventCount).map(
                    ([sev, count]) => (
                      <Badge
                        key={sev}
                        bg={SEVERITY_COLORS[sev] || "secondary"}
                        className="me-1"
                      >
                        {count} {sev}
                      </Badge>
                    )
                  )}
                </span>
              )}
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setShowCreateModal(true)}
            >
              + Manual event
            </Button>
          </div>

          {healthEvents.isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : healthEvents.data ? (
            <HealthEventsTable
              events={healthEvents.data.events}
              siteId={site.id}
              timeZone={site.timeZone}
              esysEntries={esysEntries}
              onUpdateImpactType={handleUpdateImpactType}
              onUpdateNotes={handleUpdateNotes}
              onUpdateExclusionReason={handleUpdateExclusionReason}
              onUpdateEvent={handleUpdateEvent}
              onDeleteEvent={handleDeleteEvent}
              isUpdating={updateEvent.isLoading}
              title="Health Events"
              earliestMonthInclusive={site.commerciallyOperationalDate?.slice(
                0,
                7
              )}
            />
          ) : healthEvents.isError ? (
            <div className="text-danger">Failed to load health events</div>
          ) : null}

          {healthEvents.data && (
            <AcknowledgedSolarEdgeAlertsCard
              siteId={site.id}
              acknowledgements={
                healthEvents.data.solaredgeAlertAcknowledgements
              }
              timeZone={site.timeZone}
            />
          )}

          <CreateManualEventModal
            show={showCreateModal}
            onHide={() => setShowCreateModal(false)}
            timeZone={site.timeZone}
            onSubmit={handleCreateManualEvent}
            isSubmitting={createManualEvent.isLoading}
          />
        </>
      )}
    </Container>
  );
};

export default SiteHealthStatus;
